import { Card, Typography, Alert, Divider } from 'antd'
import { 
  RobotOutlined, 
  CodeOutlined,
  RocketOutlined,
  BookOutlined
} from '@ant-design/icons'

const { Title, Text, Paragraph } = Typography

const TrainingDashboard = () => {
  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <RobotOutlined /> LLM Training Center
        </Title>
        <Text type="secondary">
          Fine-tune Qwen3-8B on Maximo database schema
        </Text>
      </div>

      {/* Info Alert */}
      <Alert
        message="Python-Based Finetuning Application"
        description="The LLM training functionality is a separate Python application located in the /finetuning directory. This dashboard page has been cleaned of demo data. Please use the command-line instructions below to start training."
        type="info"
        showIcon
        icon={<CodeOutlined />}
        style={{ marginBottom: '24px' }}
      />

      {/* Getting Started */}
      <Card
        title={
          <span>
            <RocketOutlined /> Getting Started with Finetuning
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        <Paragraph>
          The finetuning application is a complete Python-based training pipeline for fine-tuning Qwen3-8B 
          on your Maximo database schema. It supports both local training and cloud deployment on Vast.ai.
        </Paragraph>

        <Divider orientation="left">📁 Files Location</Divider>
        <Paragraph>
          <ul>
            <li><code>/finetuning</code> - Main finetuning directory</li>
            <li><code>limited_schemas.yaml</code> - Your Maximo database schema (12 tables)</li>
            <li><code>prepare_data.py</code> - Generates training data from schema</li>
            <li><code>finetune_qwen3.py</code> - Main training script</li>
            <li><code>evaluate.py</code> - Tests the fine-tuned model</li>
            <li><code>run_local.py</code> - Run the trained model locally</li>
          </ul>
        </Paragraph>

        <Divider orientation="left">🚀 Quick Start - Local Training</Divider>
        <Paragraph>
          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
{`# Navigate to finetuning directory
cd /Users/facilio/thiran/thiranai_v2/finetuning

# Install dependencies
pip install torch transformers peft bitsandbytes accelerate PyYAML wandb

# Generate training data from schema
python3 prepare_data.py

# Start training (requires GPU)
python3 finetune_qwen3.py`}
          </pre>
        </Paragraph>

        <Divider orientation="left">☁️ Cloud Training on Vast.ai</Divider>
        <Paragraph>
          <ol>
            <li>Rent a GPU on <a href="https://vast.ai" target="_blank" rel="noopener noreferrer">Vast.ai</a> (RTX 4090 recommended, ~$0.30-0.50/hr)</li>
            <li>Upload the finetuning directory:</li>
          </ol>
          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
{`scp -P <PORT> -r finetuning/ root@<IP>:/workspace/`}
          </pre>
          <ol start={3}>
            <li>SSH into Vast.ai and run setup:</li>
          </ol>
          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
{`ssh -p <PORT> root@<IP>
cd /workspace/finetuning
chmod +x setup_env.sh
./setup_env.sh

# Authenticate with Hugging Face
huggingface-cli login

# Start training
python3 finetune_qwen3.py`}
          </pre>
        </Paragraph>

        <Divider orientation="left">💡 After Training</Divider>
        <Paragraph>
          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
{`# Evaluate the model
python3 evaluate.py

# Merge adapter weights (optional)
python3 merge_adapter.py

# Run locally with interactive prompt
python3 run_local.py`}
          </pre>
        </Paragraph>
      </Card>

      {/* Documentation Links */}
      <Card
        title={
          <span>
            <BookOutlined /> Documentation
          </span>
        }
      >
        <Paragraph>
          For detailed instructions, please refer to:
        </Paragraph>
        <ul>
          <li>
            <a href="file:///Users/facilio/thiran/thiranai_v2/finetuning/README.md" target="_blank">
              <code>finetuning/README.md</code>
            </a> - Complete fine-tuning guide
          </li>
          <li>
            <a href="file:///Users/facilio/thiran/thiranai_v2/finetuning/USAGE.md" target="_blank">
              <code>finetuning/USAGE.md</code>
            </a> - Running the model locally
          </li>
          <li>
            <a href="file:///Users/facilio/thiran/thiranai_v2/finetuning/VAST_SETUP.md" target="_blank">
              <code>finetuning/VAST_SETUP.md</code>
            </a> - Vast.ai deployment guide
          </li>
        </ul>

        <Divider />

        <Paragraph type="secondary" style={{ fontSize: '12px' }}>
          <strong>Note:</strong> The frontend training dashboard is for monitoring purposes only. 
          All actual training operations are performed using the Python scripts in the <code>/finetuning</code> directory.
        </Paragraph>
      </Card>
    </div>
  )
}

export default TrainingDashboard